/**
 * Start Again Utility
 * Provides consistent "Start Again" functionality across the application
 */

import ParentMessenger, { MessageTypes } from './parent-messenger.js';
import ResetOverlay from './reset-overlay.js';

interface PrefillData {
  company?: string;
  product?: string;
  email?: string;
}

class StartAgain {
  /**
   * Execute the Start Again action
   * Resets the journey builder to initial state
   * @param prefillData - Optional data to prefill form fields on reset
   */
  static execute(prefillData?: PrefillData): void {
    console.log('Starting again - resetting journey builder', prefillData);

    // Clear relevant state if StateManager is available
    if (window.stateManager) {
      // Clear fallback state
      window.stateManager.setState('fallback', {});
      
      // Reset form state
      window.stateManager.setState('forms.marketing', {
        valid: false,
        submitted: false,
        errors: {}
      });
      
      // Clear UI states
      window.stateManager.setState('ui.loading.marketingForm', false);
      window.stateManager.setState('ui.errors.marketingForm', null);
      
      // Clear journey data
      window.stateManager.setState('data.journeys.current', null);
    }

    // Check if we're in marketing-embed context
    if (window.location.pathname.includes('marketing-embed.html')) {
      // Marketing path: notify parent to reset and reload with overlay
      ParentMessenger.send(MessageTypes.JOURNEY_RESET, {});
      
      // Show loading overlay (parent will handle the reload)
      ResetOverlay.show();
    } else {
      // Build URL with optional prefill parameters
      let targetUrl = '/';
      
      if (prefillData) {
        const params = new URLSearchParams();
        
        if (prefillData.company) {
          params.append('company', prefillData.company);
        }
        if (prefillData.product) {
          params.append('product', prefillData.product);
        }
        if (prefillData.email) {
          params.append('email', prefillData.email);
        }
        
        const queryString = params.toString();
        if (queryString) {
          targetUrl += '?' + queryString;
        }
      }
      
      // Navigate to home page with optional parameters
      window.location.href = targetUrl;
    }
  }
}

// Export for module usage
export default StartAgain;

// Make available globally for non-module scripts
declare global {
  interface Window {
    StartAgain: typeof StartAgain;
  }
}

if (typeof window !== 'undefined') {
  window.StartAgain = StartAgain;
}